import type { Language } from '@hero/schema';
import { agentCopy } from '../language/agent-copy';

/**
 * A Zod-shaped issue, structurally typed.
 *
 * Not imported from `zod` on purpose: this runs on the failure path and
 * may be handed an error that crossed a transaction boundary, a
 * serialized copy, or a `ZodError` from a different zod instance than
 * the one this package resolves. Structural matching survives all
 * three; `instanceof` survives none of them.
 */
interface IssueLike {
  code?: string;
  message?: string;
  path?: Array<string | number>;
  minimum?: number | bigint;
  maximum?: number | bigint;
  type?: string;
  expected?: string;
  received?: string;
  options?: Array<string | number>;
}

const isIssueArray = (value: unknown): value is IssueLike[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(item => typeof item === 'object' && item !== null && 'message' in item);

/**
 * Dig out Zod issues from an error, however deeply it got wrapped.
 *
 * By the time a validation failure reaches the agent it has usually
 * been through `DBService.transactions`, which catches anything it does
 * not recognise and rethrows `new ExceptionGlobal('db_transaction_error',
 * originalError)`. The issues are then two levels down, inside the
 * Nest response body's `data`. The shallow read is kept for the direct
 * case.
 */
const findIssues = (error: unknown, depth = 0): IssueLike[] | undefined => {
  if (depth > 4 || typeof error !== 'object' || error === null) return undefined;

  const record = error as Record<string, unknown>;
  // `issues` is zod's own; `fields` is the third `ExceptionGlobal`
  // argument, which callers use to forward `ZodIssue[]` deliberately.
  if (isIssueArray(record.issues)) return record.issues;
  if (isIssueArray(record.fields)) return record.fields;

  // `ExceptionGlobal` keeps the original throw on the response body.
  const response =
    typeof record.getResponse === 'function'
      ? (record.getResponse as () => unknown).call(error)
      : undefined;

  for (const nested of [response, record.data, record.response, record.cause]) {
    const found = findIssues(nested, depth + 1);
    if (found) return found;
  }
  return undefined;
};

/**
 * What to call each field when talking to the person who typed it.
 *
 * Keyed by the last named segment of the Zod path, so `orgs.0.phone`
 * and `driver.phone` both land on `phone`. Everything the agent's
 * confirmation cards can carry should have an entry — a field with no
 * label still gets reported (see {@link labelFor}), just less warmly.
 */
type FieldLabelKey =
  | 'phone'
  | 'email'
  | 'name'
  | 'full_legal_name'
  | 'plate'
  | 'plate_type'
  | 'truck_type'
  | 'id_number'
  | 'id_issue_number'
  | 'nationality'
  | 'unified_number'
  | 'employer_unified_number'
  | 'logisti_client_id'
  | 'city'
  | 'weight'
  | 'price'
  | 'fare'
  | 'quantity'
  | 'pickup_date';

const FIELD_LABELS = new Map<string, FieldLabelKey>(
  Object.entries({
    phone: 'phone',
    email: 'email',
    name: 'name',
    fullName: 'full_legal_name',
    fullLegalName: 'full_legal_name',
    plate: 'plate',
    plateType: 'plate_type',
    vehicleType: 'truck_type',
    vehicle: 'truck_type',
    id: 'id_number',
    identityNumber: 'id_number',
    // "إصدار" reads to most people as the ID number itself, so they send
    // ten digits. "نسخة" with the counter spelled out is what the card
    // actually shows and what nobody misreads.
    idIssueNumber: 'id_issue_number',
    identityIssueNumber: 'id_issue_number',
    nationality: 'nationality',
    crNumber: 'unified_number',
    registrationId: 'unified_number',
    employerCrNumber: 'employer_unified_number',
    clientId: 'logisti_client_id',
    city: 'city',
    weight: 'weight',
    price: 'price',
    fare: 'fare',
    quantity: 'quantity',
    pickupDate: 'pickup_date',
  } satisfies Record<string, FieldLabelKey>),
);

type FieldHintKey = 'phone' | 'plate' | 'id_issue_number' | 'unified_number' | 'employer_unified_number';

/**
 * Extra guidance for fields where naming the field is not enough to
 * produce a correct value on the second try. Only worth writing when
 * the right shape is genuinely non-obvious.
 */
const FIELD_HINTS = new Map<string, FieldHintKey>(
  Object.entries({
    phone: 'phone',
    plate: 'plate',
    idIssueNumber: 'id_issue_number',
    identityIssueNumber: 'id_issue_number',
    crNumber: 'unified_number',
    registrationId: 'unified_number',
    employerCrNumber: 'employer_unified_number',
  } satisfies Record<string, FieldHintKey>),
);

/** `invalid_type` / `invalid_enum_value` name the expected value in English only. */
const INVALID_TYPE: Record<Language, (issue: IssueLike) => string> = {
  en: issue => (issue.received === 'undefined' ? 'is missing' : `is not a valid ${issue.expected}`),
  ar: issue => (issue.received === 'undefined' ? 'ناقص' : 'غير صحيح'),
  ur: issue => (issue.received === 'undefined' ? 'موجود نہیں' : 'درست نہیں'),
};

const INVALID_ENUM_VALUE: Record<Language, (issue: IssueLike) => string> = {
  en: issue => `is not one of the accepted values${issue.options ? ` (${issue.options.join(', ')})` : ''}`,
  ar: () => 'ليس من القيم المقبولة',
  ur: () => 'قابلِ قبول اقدار میں سے نہیں',
};

/** What is wrong, phrased from the Zod issue rather than its English text. */
const RULES = new Map<string, (issue: IssueLike, language: Language) => string>(
  Object.entries({
    too_small: (issue: IssueLike, language: Language) =>
      agentCopy(
        language,
        issue.type === 'string'
          ? 'errors.validation.rules.too_small_string'
          : 'errors.validation.rules.too_small_number',
        { minimum: String(issue.minimum) },
      ),
    too_big: (issue: IssueLike, language: Language) =>
      agentCopy(language, 'errors.validation.rules.too_big', { maximum: String(issue.maximum) }),
    invalid_type: (issue: IssueLike, language: Language) =>
      (INVALID_TYPE[language] ?? INVALID_TYPE.en)(issue),
    invalid_enum_value: (issue: IssueLike, language: Language) =>
      (INVALID_ENUM_VALUE[language] ?? INVALID_ENUM_VALUE.en)(issue),
    invalid_string: (_issue: IssueLike, language: Language) =>
      agentCopy(language, 'errors.validation.rules.invalid_string'),
    invalid_date: (_issue: IssueLike, language: Language) =>
      agentCopy(language, 'errors.validation.rules.invalid_date'),
    custom: (_issue: IssueLike, language: Language) =>
      agentCopy(language, 'errors.validation.rules.custom'),
  }),
);

const header = (count: number, language: Language): string =>
  count === 1
    ? agentCopy(language, 'errors.validation.header_one')
    : agentCopy(language, 'errors.validation.header_many', { count });

/**
 * The last path segment that names something, e.g. `['orgs', 0, 'phone']`
 * → `phone`. Numeric indices carry no meaning for the user.
 */
const fieldOf = (issue: IssueLike): string | undefined =>
  [...(issue.path ?? [])]
    .reverse()
    .find((segment): segment is string => typeof segment === 'string');

/**
 * A readable label for a field, even one nobody wrote copy for.
 *
 * The fallback de-camel-cases the path segment (`idIssueNumber` → "id
 * issue number"). It is not translated, and that is the deliberate
 * trade: an Arabic sentence carrying one English field name still tells
 * the user exactly which box to fix, whereas the generic "something
 * went wrong" tells them nothing at all. Anything that shows up in this
 * fallback often enough to notice should get a `FIELD_LABELS` entry.
 */
const labelFor = (field: string | undefined, language: Language): string | undefined => {
  if (!field) return undefined;
  const key = FIELD_LABELS.get(field);
  if (key) return agentCopy(language, `errors.validation.field_labels.${key}`);
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .toLowerCase()
    .trim();
};

const ruleFor = (issue: IssueLike, language: Language): string => {
  const render = issue.code ? RULES.get(issue.code) : undefined;
  return render ? render(issue, language) : agentCopy(language, 'errors.validation.rules.fallback');
};

/** Only the first complaint per field — one line per box to fix. */
const dedupeByField = (issues: IssueLike[]): IssueLike[] => {
  const seen = new Set<string>();
  return issues.filter(issue => {
    const key = fieldOf(issue) ?? JSON.stringify(issue.path ?? []);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** More than this and the message stops being readable on a phone. */
const MAX_REPORTED = 4;

/**
 * Turn a validation failure into a message that names every field that
 * is wrong, says what is wrong with each, and asks for the fix — or
 * `undefined` when the error is not a validation failure at all.
 *
 * ## Why this exists
 *
 * A `ZodError` is not an `ExceptionGlobal`, so `DBService.transactions`
 * cannot recognise it and relabels it `db_transaction_error`, which the
 * classifier reads as `unknown`, which renders "Something went wrong,
 * please try again". A user who typed eight digits of a phone number
 * was told, in effect, that the computer was broken — and the honest
 * answer was sitting in `issues[0].path` the whole time.
 *
 * It reports **every** invalid field rather than the first, because
 * fixing one at a time turns a two-field mistake into four round trips
 * on WhatsApp.
 *
 * This is the last line of defence, not the first. The right place to
 * catch bad input is before the confirmation card goes out (see
 * `driverPhoneIssue`); this only handles what gets past that, and it
 * covers forms nobody has written a pre-check for yet — which is most
 * of them.
 */
export const validationErrorMessage = (
  error: unknown,
  language?: Language,
): string | undefined => {
  const issues = findIssues(error);
  if (!issues) return undefined;

  const lang = language ?? 'en';
  const reported = dedupeByField(issues).slice(0, MAX_REPORTED);

  const lines = reported.flatMap(issue => {
    const field = fieldOf(issue);
    const label = labelFor(field, lang);
    if (!label) return [];
    const hintKey = field ? FIELD_HINTS.get(field) : undefined;
    const hint = hintKey ? agentCopy(lang, `errors.validation.field_hints.${hintKey}`) : undefined;
    return [`• ${label} — ${ruleFor(issue, lang)}.${hint ? ` ${hint}` : ''}`];
  });

  // Every issue was on an unnamed path (a whole-object refinement, say).
  // There is no box to point at, so the caller's generic copy is more
  // honest than a bullet list of nothing.
  if (lines.length === 0) return undefined;

  return [
    header(lines.length, lang),
    lines.join('\n'),
    agentCopy(lang, 'errors.validation.footer'),
  ].join('\n\n');
};

/** Whether {@link validationErrorMessage} can answer this error. */
export const isValidationError = (error: unknown): boolean => !!findIssues(error);
